import { useEffect, useState } from 'react';

/** A reference to a model, which may or may not be loaded. */
export interface ModelReference<ModelType, IdKey extends keyof ModelType> {
  id: ModelType[IdKey];
  getModel(): Promise<ModelType | null>;
  requireModel(): Promise<ModelType>;
}

/**
 * A function provided by the reference implementation, which is responsible for loading the model.
 */
export type ModelReferenceLoader<ModelType, IdKey extends keyof ModelType> = (
  id: ModelType[IdKey],
) => Promise<ModelType | null>;

/**
 * A simple inlineable object that can be used to create a reference to a model. The purpose of this
 * type is to give consuming code a simple interface, and smooth over the "reference" internals.
 */
export type ModelReferenceDescriptor<ModelType, IdKey extends keyof ModelType> =
  | { id: ModelType[IdKey] }
  | { model: ModelType };

/**
 * A factory for references for a type of model. This accepts a descriptor and produces a reference.
 */
export type ModelReferenceFactory<ModelType, IdKey extends keyof ModelType> = (
  reference: ModelReferenceDescriptor<ModelType, IdKey>,
) => ModelReference<ModelType, IdKey>;

/**
 * Define a new reference factory.
 *
 * @template ModelType The type of the model that this reference will resolve.
 * @template IdKey The name of the property under `ModelType` that contains the ID of the model.
 * This should be the same as the `idField` parameter.
 * @param typeName The name of the type. This is only used to generate error messaging when a model
 * cannot be found.
 * @param idField The name of the column that contains the ID of the model, typically `id`.
 * @param loader A function that can load a model by its ID, and may return `null` if not found.
 */
export function createReferenceFactory<ModelType, IdKey extends keyof ModelType>(
  typeName: string,
  idField: IdKey,
  loader: ModelReferenceLoader<ModelType, IdKey>,
): ModelReferenceFactory<ModelType, IdKey> {
  return ( descriptor ) => {
    // TODO: this object would make more sense as a class than built this way.
    return {
      id: 'model' in descriptor ? descriptor.model[idField] : descriptor.id,

      getModel: () => ( 'model' in descriptor ? Promise.resolve( descriptor.model ) : loader( descriptor.id ) ),

      requireModel: async () => {
        if ( 'model' in descriptor ) {
          return descriptor.model;
        }

        const model = await loader( descriptor.id );

        if ( model ) {
          return model;
        }

        throw new Error( `${typeName} with ID ${descriptor.id} not found.` );
      },
    };
  };
}

/** The result of the `useReference` hook. */
export interface UseReferenceResult<ModelType> {
  model: ModelType | undefined;
  error: Error | undefined;
}

/**
 * A custom React hook that will resolve a reference into a model (or error).
 *
 * @return Returns an object containing `{ model, error }`. The model may be populated with the
 * resolved model, but may also be `null` or `undefined`. These have distinct meanings:
 *
 *  - If `null`, the query completed but the model was not found.
 *  - If `undefined`, the query has not completed yet. This could indicate that it's still loading
 *    (`error` will also be undefined), or that it encountered a terminal error and will never
 *    resolve (`error` will be defined).
 */
export const useReference = <ModelType, IdKey extends keyof ModelType>(
  reference: ModelReference<ModelType, IdKey>,
): UseReferenceResult<ModelType | null> => {
  const [model, setModel] = useState<ModelType | null | undefined>();
  const [error, setError] = useState<Error | undefined>();

  useEffect( () => {
    async function doFetch() {
      try {
        setModel( await reference.getModel() );
      } catch ( err ) {
        setError( err instanceof Error ? err : new Error( `Unknown error type while loading reference: ${err}` ) );
      }
    }

    doFetch();
  }, [reference] );

  return { model, error };
};
